import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { AdminNav } from "../../components/admin/AdminNav";
import { formatDate } from "../../lib/format";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ExternalLink, Loader2 } from "lucide-react";

export default function AdminArticlesPage() {
  const [articles, setArticles] = useState(null);

  const load = () => api.get("/admin/articles").then((r) => setArticles(r.data)).catch(() => setArticles([]));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/admin/articles/${id}`);
      toast.success("Articolo eliminato");
      load();
    } catch { toast.error("Errore durante l'eliminazione"); }
  };

  return (
    <div className="min-h-screen bg-[#F4F8FF]">
      <AdminNav />
      <div className="px-6 md:px-10 max-w-[1400px] mx-auto py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl font-light text-[#0B1220]">Blog</h1>
            <p className="text-[#5C6678] mt-1">Crea, importa da Word e pubblica articoli ottimizzati SEO.</p>
          </div>
          <Link
            to="/admin/blog/nuovo"
            data-testid="new-article-btn"
            className="inline-flex items-center gap-2 bg-[#1452F0] text-[#F4F8FF] hover:bg-[#0E3DC0] transition-colors rounded-sm px-6 py-3 text-sm font-medium"
          >
            <Plus className="h-4 w-4" /> Nuovo articolo
          </Link>
        </div>

        {articles === null ? (
          <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#1452F0]" /></div>
        ) : articles.length === 0 ? (
          <div className="py-20 text-center text-[#5C6678]">Nessun articolo. Crea o importa il primo articolo.</div>
        ) : (
          <div className="bg-white rounded-sm border border-[#D5DEEE] overflow-hidden">
            <table className="w-full text-sm" data-testid="articles-table">
              <thead>
                <tr className="border-b border-[#D5DEEE] text-left text-[#5C6678] text-xs uppercase tracking-wider">
                  <th className="px-5 py-4 font-medium">Articolo</th>
                  <th className="px-5 py-4 font-medium hidden md:table-cell">Categoria</th>
                  <th className="px-5 py-4 font-medium hidden md:table-cell">Data</th>
                  <th className="px-5 py-4 font-medium">Stato</th>
                  <th className="px-5 py-4 font-medium text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((a) => (
                  <tr key={a.id} className="border-b border-[#D5DEEE] last:border-0 hover:bg-[#E9EFFA]/50" data-testid={`article-row-${a.id}`}>
                    <td className="px-5 py-4">
                      <div className="font-medium text-[#0B1220]">{a.h1}</div>
                      <div className="text-xs text-[#5C6678]">/blog/{a.slug}</div>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell text-[#5C6678]">{a.category || "—"}</td>
                    <td className="px-5 py-4 hidden md:table-cell text-[#5C6678]">{formatDate(a.published_at || a.created_at)}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${a.status === "published" ? "bg-[#1452F0]/15 text-[#1452F0]" : "bg-[#5C6678]/15 text-[#5C6678]"}`}>
                        {a.status === "published" ? "Pubblicato" : "Bozza"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        {a.status === "published" && (
                          <a href={`/blog/${a.slug}`} target="_blank" rel="noopener noreferrer" title="Anteprima" className="p-2 rounded-sm hover:bg-[#D5DEEE] text-[#5C6678]" data-testid={`article-preview-${a.id}`}>
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                        <Link to={`/admin/blog/${a.id}`} title="Modifica" className="p-2 rounded-sm hover:bg-[#D5DEEE] text-[#1452F0]" data-testid={`article-edit-${a.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Link>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button title="Elimina" className="p-2 rounded-sm hover:bg-[#A63C3C]/10 text-[#A63C3C]" data-testid={`article-delete-${a.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#F4F8FF]">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="font-heading">Eliminare l'articolo?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Questa azione non può essere annullata. L'articolo "{a.h1}" verrà eliminato definitivamente.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annulla</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(a.id)} className="bg-[#A63C3C] hover:bg-[#8a3030]" data-testid={`article-confirm-delete-${a.id}`}>
                                Elimina
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
